import React, { useSyncExternalStore } from 'react'

/**
 * Which column the pointer is in, anywhere in a grid — its header, a cell, a group cell — so a
 * header can show its ⋮ menu button while you're in its column, not only on the header itself.
 *
 * A small store outside React rather than state: the grid reports the column once per pointer
 * move from a single listener, and only the headers whose answer changes (the column left and
 * the column entered) re-render. Keys are `${gridNumber}:${columnId}`, so two grids showing the
 * same table don't light each other's headers.
 */
let hoveredColumnKey: string | null = null
const listeners = new Set<() => void>()

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const setHoveredColumn = (key: string | null): void => {
  if (key === hoveredColumnKey) return
  hoveredColumnKey = key
  listeners.forEach((listener) => listener())
}

/** Report the column under the pointer (or keyboard focus), from an event anywhere in grid `gridNumber`. */
export const trackHoveredColumn = (gridNumber: number, event: React.SyntheticEvent<HTMLElement>): void => {
  const columnId = (event.target as HTMLElement).closest('[data-columnid]')?.getAttribute('data-columnid')
  setHoveredColumn(columnId ? `${gridNumber}:${columnId}` : null)
}

/** Whether the pointer is anywhere in column `columnId` of grid `gridNumber`. */
export const useIsColumnHovered = (gridNumber: number, columnId: string | undefined): boolean =>
  useSyncExternalStore(
    subscribe,
    () => columnId !== undefined && hoveredColumnKey === `${gridNumber}:${columnId}`,
    () => false
  )
